import { Link } from '@tanstack/react-router';
import { useId, type ReactElement } from 'react';
import type { PieceDuplicateMatch } from '@/lib/queries/pieces';

export type DuplicateMatchPanelProps = {
  matches: PieceDuplicateMatch[];
  onDismiss: () => void;
};

const MAX_VISIBLE_MATCHES = 3;

export function DuplicateMatchPanel({
  matches,
  onDismiss,
}: DuplicateMatchPanelProps): ReactElement | null {
  const headingId = useId();
  if (matches.length === 0) return null;

  const visible = matches.slice(0, MAX_VISIBLE_MATCHES);
  const headerLabel =
    matches.length === 1 ? '1 match' : `${matches.length} matches`;

  return (
    <section
      aria-labelledby={headingId}
      className="rounded-[var(--r-md)] border border-[var(--rule)] bg-[var(--bg-soft)] p-4"
    >
      <header className="flex items-start justify-between gap-3">
        <h3
          id={headingId}
          className="text-sm font-semibold text-[var(--ink)]"
        >
          <span className="sr-only">Possible duplicates</span>
          <span aria-hidden="true">Possible duplicate · {headerLabel}</span>
        </h3>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss possible duplicates"
          className="text-[var(--ink-3)] hover:text-[var(--ink)]"
        >
          ×
        </button>
      </header>
      <ul className="mt-3 flex flex-col gap-3">
        {visible.map((match) => (
          <DuplicateMatchRow key={match.pieceId} match={match} />
        ))}
      </ul>
    </section>
  );
}

function DuplicateMatchRow({ match }: { match: PieceDuplicateMatch }): ReactElement {
  const arrangementsLabel =
    match.arrangementCount === 1
      ? '1 arrangement'
      : `${match.arrangementCount} arrangements`;
  const formsLabel = match.forms.join(', ');
  const metaLabel =
    formsLabel.length > 0 ? `${formsLabel} · ${arrangementsLabel}` : arrangementsLabel;

  return (
    <li className="flex flex-col gap-1 rounded border border-[var(--rule)] bg-[var(--bg-surface)] p-3">
      <div className="font-display text-sm font-semibold text-[var(--ink)]">
        {match.prettyName}
      </div>
      {match.composerName && match.composerName.length > 0 && (
        <div className="text-xs text-[var(--ink-3)]">{match.composerName}</div>
      )}
      <div className="text-xs text-[var(--ink-3)]">{metaLabel}</div>
      <div className="mt-1 flex gap-3 text-xs">
        <Link
          to="/library/$pieceId"
          params={{ pieceId: match.pieceId }}
          aria-label={`Open ${match.prettyName}`}
          className="text-[var(--brand-ink)] underline"
        >
          Open
        </Link>
        <Link
          to="/library/$pieceId"
          params={{ pieceId: match.pieceId }}
          hash="add-setting"
          aria-label={`Add setting to ${match.prettyName}`}
          className="text-[var(--brand-ink)] underline"
        >
          Add setting to existing
        </Link>
      </div>
    </li>
  );
}
